import { useController } from "react-hook-form";

import { FileUpload, useFileUpload } from "src/base-components/FileUpload";
import { MAX_COPILOT_CONTEXT_FILES } from "src/cases/templates/formUtils/schema";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { AcceptedFileTypes } from "src/utils/files";

// Narrower than DEFAULT_ACCEPTED_FILE_TYPES: only formats the copilot agent
// can consume in its context window (AGNT-959).
const COPILOT_CONTEXT_ACCEPTED_FILE_TYPES: AcceptedFileTypes = {
  "text/plain": [".txt"],
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "text/csv": [".csv"],
  "application/json": [".json"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

const COPILOT_CONTEXT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

type CopilotContextFilesFieldProps = {
  disabled?: boolean;
};

/**
 * Manages the template copilot context files. Files are always uploaded to the
 * live environment — templates are environment-agnostic, and the copilot reads
 * context files from the live files API.
 */
export const CopilotContextFilesField: React.FC<
  CopilotContextFilesFieldProps
> = ({ disabled = false }) => {
  const { field } = useController<
    CaseTemplateFormValues,
    "attributes.copilot_settings.context_file_ids"
  >({ name: "attributes.copilot_settings.context_file_ids" });

  const { fileUploadProps } = useFileUpload({
    multiple: true,
    env: "live",
    source: "case-copilot-context",
    value: field.value,
    onChange: field.onChange,
    disabled,
  });

  return (
    <FileUpload
      {...fileUploadProps}
      accept={COPILOT_CONTEXT_ACCEPTED_FILE_TYPES}
      dataLoc="copilot-context-files"
      description="Files uploaded here apply to Live Cases only. Supported types: txt, pdf, images, csv, json, xlsx"
      maxFiles={MAX_COPILOT_CONTEXT_FILES}
      maxSize={COPILOT_CONTEXT_MAX_FILE_SIZE_BYTES}
    />
  );
};
